import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, BarChart3, Target, ArrowUpRight, Sparkles } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";

const AIMetrics = () => {
  const { t, language } = useLanguage();

  const brandData = [
    { week: "4 Aug", yourBrand: 38, competitorA: 78, competitorB: 82, competitorC: 52 },
    { week: "11 Aug", yourBrand: 47, competitorA: 80, competitorB: 78, competitorC: 54 },
    { week: "18 Aug", yourBrand: 59, competitorA: 76, competitorB: 82, competitorC: 56 },
    { week: "25 Aug", yourBrand: 68, competitorA: 78, competitorB: 80, competitorC: 58 },
    { week: "1 Sep", yourBrand: 81, competitorA: 80, competitorB: 84, competitorC: 54 },
    { week: "8 Sep", yourBrand: 96, competitorA: 82, competitorB: 86, competitorC: 56 },
  ];

  const competitorRanking = [
    { rank: 1, name: language === "fr" ? "Votre Marque" : "Your Brand", visibility: 39, change: "+12%" },
    { rank: 2, name: "Concurrent A", visibility: 33, change: "+5%" },
    { rank: 3, name: "Concurrent B", visibility: 24, change: "-2%" },
    { rank: 4, name: "Concurrent C", visibility: 23, change: "+1%" },
  ];

  const promptPerformance = [
    { prompt: language === "fr" ? "Meilleures solutions GEO e-..." : "Best GEO solutions for e-...", mentions: 15, avg: 4.1, score: 0.3 },
    { prompt: language === "fr" ? "Outils d'optimisation IA pour marques" : "AI optimization tools for brands", mentions: 23, avg: 4.8, score: 0.2 },
    { prompt: language === "fr" ? "Services de visibilité IA en France" : "AI visibility services in France", mentions: 18, avg: 2.4, score: 0.5 },
  ];

  return (
    <section className="py-12 lg:py-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200/70 border border-slate-300/50 backdrop-blur-sm mb-4">
            <Sparkles className="w-4 h-4 text-sky-500" />
            <span className="text-sm font-medium text-sky-600">
              {language === "fr" ? "Dashboard Analytics IA" : "AI Analytics Dashboard"}
            </span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold mb-4 text-slate-800">
            {language === "en" ? (
              <>
                <span className="bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">AI Visibility</span> Metrics
              </>
            ) : (
              <>
                Métriques de <span className="bg-gradient-to-r from-sky-400 via-blue-500 to-sky-400 bg-clip-text text-transparent animate-gradient-shine">visibilité IA</span>
              </>
            )}
          </h2>
          
          <p className="text-base text-slate-500 max-w-xl mx-auto mb-6">
            {language === "fr" 
              ? "Visualisez votre performance IA en temps réel avec des métriques avancées."
              : "Visualize your AI performance in real-time with advanced metrics."}
          </p>
          
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-semibold px-6 py-5 rounded-full shadow-lg shadow-sky-500/25 transition-all duration-300 hover:scale-105"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t("approach.cta")}
            <ArrowUpRight className="ml-2 h-5 w-5" />
          </Button>
        </AnimatedSection>

        {/* Top Row - Brand Visibility & Competitor Tracking */}
        <div className="grid lg:grid-cols-2 gap-5 mb-5">
          {/* Brand Visibility Chart */}
          <AnimatedSection animation="fade-up" delay={0.1}>
            <div className="group relative p-5 rounded-2xl card-highlight-blue transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-sky-100 to-blue-100 border border-sky-200/50">
                      <TrendingUp className="h-5 w-5 text-sky-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {t("metrics.brand.title")}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">{t("metrics.brand.desc")}</p>
                </div>

                {/* Line Chart */}
                <div className="relative h-48 lg:h-56">
                  <svg className="w-full h-full" viewBox="0 0 600 200">
                    {/* Gradient definitions */}
                    <defs>
                      <linearGradient id="brandGradientLight" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#0ea5e9" />
                        <stop offset="50%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#0ea5e9" />
                      </linearGradient>
                      <linearGradient id="areaGradientLight" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
                      </linearGradient>
                      <filter id="glowFilter" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Grid lines with fade-in animation */}
                    {[0, 25, 50, 75, 100].map((y, i) => (
                      <line
                        key={i}
                        x1="40"
                        y1={180 - y * 1.6}
                        x2="580"
                        y2={180 - y * 1.6}
                        stroke="rgba(148,163,184,0.2)"
                        strokeDasharray="4 4"
                        style={{
                          opacity: 0,
                          animation: `fadeIn 0.5s ease-out ${i * 0.1}s forwards`
                        }}
                      />
                    ))}

                    {/* Area fill with pulse animation */}
                    <path
                      d={`M70,${180 - brandData[0].yourBrand * 1.6} ${brandData.map((d, i) => `L${70 + i * 100},${180 - d.yourBrand * 1.6}`).join(" ")} L570,180 L70,180 Z`}
                      fill="url(#areaGradientLight)"
                      style={{ 
                        opacity: 0,
                        animation: 'fadeIn 1s ease-out 0.8s forwards, chartAreaPulse 3s ease-in-out 2s infinite'
                      }}
                    />

                    {/* Your Brand Line with glow animation */}
                    <polyline
                      points={brandData.map((d, i) => `${70 + i * 100},${180 - d.yourBrand * 1.6}`).join(" ")}
                      fill="none"
                      stroke="url(#brandGradientLight)"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#glowFilter)"
                      style={{
                        strokeDasharray: 1000,
                        strokeDashoffset: 1000,
                        animation: 'drawLine 2s ease-out forwards, chartLineGlow 2s ease-in-out 2.5s infinite'
                      }}
                    />
                    
                    {/* Data points with bounce and pulse animations */}
                    {brandData.map((d, i) => (
                      <g key={i}>
                        {/* Outer glow circle */}
                        <circle
                          cx={70 + i * 100}
                          cy={180 - d.yourBrand * 1.6}
                          r="10"
                          fill="rgba(14, 165, 233, 0.15)"
                          style={{
                            opacity: 0,
                            transformOrigin: `${70 + i * 100}px ${180 - d.yourBrand * 1.6}px`,
                            animation: `chartPointBounce 0.6s ease-out ${0.5 + i * 0.15}s forwards, chartPointPulse 2s ease-in-out ${2 + i * 0.2}s infinite`
                          }}
                        />
                        {/* Main point */}
                        <circle
                          cx={70 + i * 100}
                          cy={180 - d.yourBrand * 1.6}
                          r="5"
                          fill="white"
                          stroke="url(#brandGradientLight)"
                          strokeWidth="2.5"
                          style={{
                            opacity: 0,
                            transformOrigin: `${70 + i * 100}px ${180 - d.yourBrand * 1.6}px`,
                            animation: `chartPointBounce 0.6s ease-out ${0.5 + i * 0.15}s forwards`
                          }}
                        />
                        {/* Value label on hover-like state for last point */}
                        {i === brandData.length - 1 && (
                          <g style={{ animation: 'chartValueFloat 2s ease-in-out 2s infinite' }}>
                            <rect
                              x={70 + i * 100 - 20}
                              y={180 - d.yourBrand * 1.6 - 32}
                              width="40"
                              height="20"
                              rx="6"
                              fill="rgba(14, 165, 233, 0.9)"
                              style={{
                                opacity: 0,
                                animation: 'fadeIn 0.5s ease-out 2s forwards'
                              }}
                            />
                            <text
                              x={70 + i * 100}
                              y={180 - d.yourBrand * 1.6 - 18}
                              textAnchor="middle"
                              className="text-[11px] font-bold fill-white"
                              style={{
                                opacity: 0,
                                animation: 'fadeIn 0.5s ease-out 2.1s forwards'
                              }}
                            >
                              {d.yourBrand}%
                            </text>
                          </g>
                        )}
                      </g>
                    ))}
                    
                    {/* Competitor lines */}
                    <polyline
                      points={brandData.map((d, i) => `${70 + i * 100},${180 - d.competitorA * 1.6}`).join(" ")}
                      fill="none"
                      stroke="rgba(168,85,247,0.5)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />
                    <polyline
                      points={brandData.map((d, i) => `${70 + i * 100},${180 - d.competitorB * 1.6}`).join(" ")}
                      fill="none"
                      stroke="rgba(34,197,94,0.4)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />
                    <polyline
                      points={brandData.map((d, i) => `${70 + i * 100},${180 - d.competitorC * 1.6}`).join(" ")}
                      fill="none"
                      stroke="rgba(148,163,184,0.4)"
                      strokeWidth="2"
                      strokeDasharray="6 4"
                    />

                    {/* Labels */}
                    {brandData.map((d, i) => (
                      <text
                        key={i}
                        x={70 + i * 100}
                        y="198"
                        textAnchor="middle"
                        className="text-[10px] fill-slate-400"
                      >
                        {d.week}
                      </text>
                    ))}
                  </svg>
                </div>
                
                {/* Legend */}
                <div className="flex gap-4 justify-center mt-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-sky-400 to-blue-500" />
                    <span className="text-xs text-slate-700 font-medium">{language === "fr" ? "Votre Marque" : "Your Brand"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-400/60" />
                    <span className="text-xs text-slate-500">Concurrent A</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-400/50" />
                    <span className="text-xs text-slate-500">Concurrent B</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Competitor Tracking */}
          <AnimatedSection animation="fade-up" delay={0.2}>
            <div className="group relative p-5 rounded-2xl card-highlight-purple transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200/50">
                      <Users className="h-5 w-5 text-purple-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {t("metrics.competitor.title")}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">{t("metrics.competitor.desc")}</p>
                </div>

                <div className="space-y-2">
                  {competitorRanking.map((item) => (
                    <div
                      key={item.rank}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${
                        item.rank === 1
                          ? "bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200/60"
                          : "bg-slate-50/80 border border-slate-200/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          item.rank === 1 
                            ? "bg-gradient-to-br from-sky-400 to-blue-500 text-white shadow-lg shadow-sky-500/30" 
                            : "bg-slate-100 text-slate-500"
                        }`}>
                          #{item.rank}
                        </div>
                        <div>
                          <div className={`font-medium text-sm ${item.rank === 1 ? "text-slate-800" : "text-slate-600"}`}>
                            {item.name}
                          </div>
                          <div className={`text-xs ${item.change.startsWith("+") ? "text-green-500" : "text-red-500"}`}>
                            {item.change}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${item.rank === 1 ? "text-sky-500" : "text-slate-500"}`}>
                          {item.visibility}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Bottom Row - Sentiment, Ranking, Prompt Performance */}
        <div className="grid md:grid-cols-3 gap-5">
          {/* Sentiment Score */}
          <AnimatedSection animation="fade-up" delay={0.3}>
            <div className="group relative p-5 rounded-2xl card-highlight-green transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-100 to-green-100 border border-emerald-200/50">
                      <BarChart3 className="h-5 w-5 text-emerald-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {t("metrics.sentiment.title")}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">{t("metrics.sentiment.desc")}</p>
                </div>

                <div className="flex items-center justify-center gap-4 mt-4">
                  {[
                    { value: 52, label: language === "fr" ? "Positif" : "Positive", color: "bg-green-100 text-green-500 border-green-200" },
                    { value: 33, label: language === "fr" ? "Neutre" : "Neutral", color: "bg-yellow-100 text-yellow-500 border-yellow-200" },
                    { value: 15, label: language === "fr" ? "Négatif" : "Negative", color: "bg-red-100 text-red-400 border-red-200" },
                  ].map((sentiment, i) => (
                    <div key={i} className="text-center">
                      <div className={`w-14 h-14 lg:w-16 lg:h-16 rounded-xl ${sentiment.color} border-2 flex items-center justify-center mb-2`}>
                        <span className="text-lg lg:text-xl font-bold">{sentiment.value}</span>
                      </div>
                      <span className="text-xs text-slate-500">{sentiment.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Ranking Position */}
          <AnimatedSection animation="fade-up" delay={0.4}>
            <div className="group relative p-5 rounded-2xl card-highlight-orange transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200/50">
                      <Target className="h-5 w-5 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {t("metrics.ranking.title")}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">{t("metrics.ranking.desc")}</p>
                </div>

                <div className="flex items-end justify-center gap-3 mt-4 h-28">
                  {[
                    { position: "#2", height: "h-16", color: "bg-gradient-to-b from-slate-300 via-slate-400 to-slate-350 shadow-lg" },
                    { position: "#1", height: "h-24", color: "bg-gradient-to-b from-yellow-300 via-amber-400 to-amber-450 shadow-lg shadow-amber-200/30" },
                    { position: "#3", height: "h-12", color: "bg-gradient-to-b from-amber-500 via-amber-600 to-amber-650 shadow-lg" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className={`w-14 lg:w-16 ${item.height} ${item.color} rounded-t-xl flex items-center justify-center cursor-pointer transition-all duration-300 hover:scale-105 hover:-translate-y-1 hover:shadow-xl`}
                    >
                      <span className="text-white font-bold text-sm drop-shadow-sm">{item.position}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Prompt Performance */}
          <AnimatedSection animation="fade-up" delay={0.5}>
            <div className="group relative p-5 rounded-2xl card-highlight-pink transition-all duration-500 overflow-hidden h-full">
              {/* Glass shine effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
              </div>
              <div className="relative z-10">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-xl bg-gradient-to-br from-pink-100 to-rose-100 border border-pink-200/50">
                      <TrendingUp className="h-5 w-5 text-pink-500" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-slate-800">
                      {t("metrics.prompt.title")}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500">{t("metrics.prompt.desc")}</p>
                </div>

                <div className="space-y-2">
                  {promptPerformance.map((item, i) => (
                    <div
                      key={i}
                      className="p-3 rounded-xl bg-slate-50/80 border border-slate-200/50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700 truncate max-w-[160px]">{item.prompt}</span>
                        <span className="text-sm font-bold text-green-500">↑{item.score}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{item.mentions} mentions</span>
                        <span>⭐ #{item.avg}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default AIMetrics;
